import React, { useEffect, useRef, useState } from "react";
import TourCard from "@components/common/TourCard";
import PostCard from "@components/common/PostCard";
import { PostCardFragment, TourCardFragment } from "@graphql/types";
import Icon from "@components/ui/Icon";
import { NeedMoreLinkCard } from "./needMoreCard";
import { Title } from "@components/ui/Title";

interface TourCarouselProps {
  tours: TourCardFragment[];
  noPaddingOnLeft?: boolean;
}

interface PostCarouselProps {
  posts: PostCardFragment[];
  title?: string;
  noPaddingOnLeft?: boolean;
  needMoreLink?: string;
  scrollFull?: boolean;
}

const TourCarousel: React.FC<TourCarouselProps> = ({
  tours,
  noPaddingOnLeft,
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  //for deteching the screen size on mount and when resized
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint 768px
      updateScrollPosition();
    };

    const updateScrollPosition = () => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        setAtStart(scrollLeft === 0);
        setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1); // -1 for rounding errors
      }
    };

    handleResize();
    updateScrollPosition();

    window.addEventListener("resize", handleResize);
    const carousel = carouselRef.current;
    carousel?.addEventListener("scroll", updateScrollPosition);

    return () => {
      window.removeEventListener("resize", handleResize);
      carousel?.removeEventListener("scroll", updateScrollPosition);
    };
  }, []);

  const scroll = (direction: "prev" | "next") => {
    if (carouselRef.current) {
      const offsetMovement =
        carouselRef.current?.scrollWidth /
          (carouselRef.current?.clientWidth >= 767
            ? tours.length / 3
            : tours.length) || 500;
      const scrollAmount = isMobile ? offsetMovement : offsetMovement; // Adjust for smooth scrolling
      carouselRef.current.scrollBy({
        left: direction === "next" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="relative w-full max-w-6xl mx-auto">
      {/* Scroll Left Button */}
      {!atStart && (
        <button
          className="absolute left-0 top-1/2 -translate-y-1/2 transform bg-gray-700 text-white p-2 rounded-full shadow-md z-10"
          onClick={() => scroll("prev")}
        >
          {/* ◀ */}
          <Icon icon="arrowBack" />
        </button>
      )}

      {/* Carousel Container */}
      <div ref={carouselRef} className="flex overflow-x-auto py-4 no-scrollbar">
        {tours.map((tour, index) => (
          <div
            key={tour.id}
            className={`w-90-vw md:w-1/4 flex-shrink-0 ${index === 0 && noPaddingOnLeft ? "ml-0" : "ml-4"}`}
          >
            <TourCard tour={tour} variant="vertical" showUser />
          </div>
        ))}
      </div>

      {/* Scroll Right Button */}
      {!atEnd && (
        <button
          className="absolute right-0 top-1/2 -translate-y-1/2 transform bg-gray-700 text-white p-2 rounded-full shadow-md z-10"
          onClick={() => scroll("next")}
        >
          {/* ▶ */}
          <Icon icon="arrowForward" />
        </button>
      )}
    </div>
  );
};

const PostCarousel: React.FC<PostCarouselProps> = ({
  posts,
  title,
  needMoreLink,
  scrollFull = true,
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  //for deteching the screen size on mount and when resized
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint 768px
      updateScrollPosition();
    };

    const updateScrollPosition = () => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        setAtStart(scrollLeft === 0);
        setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1); // -1 for rounding errors
      }
    };

    handleResize();
    updateScrollPosition();

    window.addEventListener("resize", handleResize);
    const carousel = carouselRef.current;
    carousel?.addEventListener("scroll", updateScrollPosition);

    return () => {
      window.removeEventListener("resize", handleResize);
      carousel?.removeEventListener("scroll", updateScrollPosition);
    };
  }, []);

  const scroll = (direction: "prev" | "next") => {
    if (carouselRef.current) {
      let offsetMovement = 500;
      if (scrollFull && !isMobile) {
        offsetMovement = carouselRef.current?.clientWidth + 16; // '+16 for 16px (gap-4) as applied in scrollable container'
      } else {
        offsetMovement =
          carouselRef.current?.scrollWidth /
            (carouselRef.current?.clientWidth >= 767
              ? posts.length / 3
              : posts.length) || 500;
      }

      const scrollAmount = isMobile ? offsetMovement : offsetMovement; // Adjust for smooth scrolling
      carouselRef.current.scrollBy({
        left: direction === "next" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="mb-10 w-full">
      {title && (
        <Title as="h2" size="md" divider>
          {title}
        </Title>
      )}

      <div className="relative w-full max-w-6xl mx-auto">
        {/* Scroll Left Button */}
        {!atStart && posts.length > 2 && (
          <button
            className="hidden md:flex absolute left-0 top-1/2 -mt-4 -translate-y-1/2 transform bg-gray-700 text-white p-1 rounded-full shadow-md z-10"
            onClick={() => scroll("prev")}
          >
            {/* ◀ */}
            <Icon icon="arrowBack" />
          </button>
        )}

        {/* Carousel Container */}
        <div
          ref={carouselRef}
          className={`flex overflow-x-auto pb-4 md:pb-0 no-scrollbar -mr-4 md:mr-0 ${!atStart ? "-mx-4 pr-4 md:ml-0 md:pr-0" : ""}`}
        >
          <div
            className="flex md:grid gap-4 pb-2 md:w-full"
            style={{
              gridTemplateColumns: `repeat(${posts.length + 1}, 31.70%)`,
            }}
          >
            {posts.map((post) => (
              <div key={post.id} className={`flex w-75-vw md:w-full`}>
                <PostCard
                  key={post.id}
                  post={post}
                  showUser={true}
                  orientation={"vertical"}
                  cardStyle={`mb-0`}
                />
              </div>
            ))}

            {needMoreLink && (
              <div className={`flex w-75-vw md:w-full`}>
                <NeedMoreLinkCard
                  isMobile={isMobile}
                  needMoreLink={needMoreLink}
                />
              </div>
            )}
          </div>
        </div>

        {/* Scroll Right Button */}
        {!atEnd && posts.length > 2 && (
          <button
            className="hidden md:flex absolute right-0 top-1/2 -mt-4 -translate-y-1/2 transform bg-gray-700 text-white p-1 rounded-full shadow-md z-10"
            onClick={() => scroll("next")}
          >
            {/* ▶ */}
            <Icon icon="arrowForward" />
          </button>
        )}
      </div>
    </div>
  );
};

export { TourCarousel, PostCarousel };
